import { useCallback, useEffect, useState } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import axios from 'axios'
import ArrowDownTrayIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon'
import BanknotesIcon from '@heroicons/react/24/solid/BanknotesIcon'
import ShoppingBagIcon from '@heroicons/react/24/solid/ShoppingBagIcon'
import UsersIcon from '@heroicons/react/24/solid/UsersIcon'
import { Alert, Box, Container, Unstable_Grid2 as Grid } from '@mui/material'
import { Layout as DashboardLayout } from 'src/layouts/dashboard/layout'
import { PageHeader } from 'src/components/page-header'
import { PatternKpiCard } from 'src/sections/pattern-dashboard/pattern-kpi-card'
import { PatternTrendChart } from 'src/sections/pattern-dashboard/pattern-trend-chart'
import { PatternDonutChart } from 'src/sections/pattern-dashboard/pattern-donut-chart'
import { PatternBreedRanking } from 'src/sections/pattern-dashboard/pattern-breed-ranking'
import { PatternLatestOrders } from 'src/sections/pattern-dashboard/pattern-latest-orders'
import { PatternRangeSelector, CUSTOM_RANGE } from 'src/sections/pattern-dashboard/pattern-range-selector'

const LATEST_ORDERS_LIMIT = 5
const DEFAULT_RANGE_DAYS = 30

/** garment 種別の表示名。未知の種別はそのまま出す */
const GARMENT_LABELS = { tank_top: 'タンクトップ' }

/** 決済方法の表示名。fincode の pay_type（Card / Paypay）を想定 */
const PAY_TYPE_LABELS = { Card: 'クレカ', Paypay: 'PayPay', unknown: '不明' }

const formatYen = (value) => `¥${Number(value ?? 0).toLocaleString()}`

/** 本日を含む過去 days 日を JST で算出する（サーバーの集計軸に合わせる） */
const buildRange = (days) => {
  const nowJst = new Date(Date.now() + 9 * 60 * 60 * 1000)
  const to = nowJst.toISOString().slice(0, 10)
  const from = new Date(nowJst.getTime() - (days - 1) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

  return { from, to }
}

/** ローカル日付（DatePicker が返す Date）を、時差の影響なく 'YYYY-MM-DD' にする */
const toLocalDateString = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const Page = () => {
  const router = useRouter()
  // rangeMode: プリセット選択中は日数(number)、カスタム範囲中は CUSTOM_RANGE
  const [rangeMode, setRangeMode] = useState(DEFAULT_RANGE_DAYS)
  const [customFrom, setCustomFrom] = useState(null)
  const [customTo, setCustomTo] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [latestOrders, setLatestOrders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isOrdersLoading, setIsOrdersLoading] = useState(true)
  const [error, setError] = useState(null)

  const handleError = useCallback(
    (e) => {
      if (e.response?.status === 401) {
        router.push('/login')
        return
      }
      setError('データの取得に失敗しました。時間をおいて再度お試しください。')
    },
    [router],
  )

  // 表示対象の期間。プリセットは即確定、カスタムは from/to が両方揃ってから確定する
  const activeRange =
    rangeMode === CUSTOM_RANGE
      ? customFrom && customTo
        ? { from: toLocalDateString(customFrom), to: toLocalDateString(customTo) }
        : null
      : buildRange(rangeMode)

  const rangeFrom = activeRange?.from
  const rangeTo = activeRange?.to

  // 期間依存の集計。期間（プリセット / カスタムの確定範囲）の変更ごとに取り直す
  const fetchDashboard = useCallback(async () => {
    // カスタムで日付が未確定のあいだは取得しない
    if (!rangeFrom || !rangeTo) return

    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/pattern/dashboard`, {
        withCredentials: true,
        params: { from: rangeFrom, to: rangeTo },
      })
      setDashboard(res.data)
    } catch (e) {
      handleError(e)
    } finally {
      setIsLoading(false)
    }
  }, [rangeFrom, rangeTo, handleError])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const handleRangeModeChange = (next) => {
    // カスタムに切り替えた初回は、直前のプリセット範囲を初期値として埋めておく
    if (next === CUSTOM_RANGE && !customFrom && !customTo) {
      const preset = buildRange(typeof rangeMode === 'number' ? rangeMode : DEFAULT_RANGE_DAYS)
      setCustomFrom(new Date(`${preset.from}T00:00:00`))
      setCustomTo(new Date(`${preset.to}T00:00:00`))
    }
    setRangeMode(next)
  }

  const handleCustomChange = (from, to) => {
    setCustomFrom(from)
    setCustomTo(to)
  }

  // 最新注文は期間に依存しないため初回のみ取得する
  useEffect(() => {
    axios
      .get(`${process.env.NEXT_PUBLIC_API_URL}/pattern/orders`, {
        withCredentials: true,
        params: { limit: LATEST_ORDERS_LIMIT, offset: 0 },
      })
      .then((res) => setLatestOrders(res.data.orders ?? []))
      .catch(handleError)
      .finally(() => setIsOrdersLoading(false))
  }, [handleError])

  const summary = dashboard?.summary
  const breakdown = dashboard?.breakdown

  const garmentItems = (breakdown?.garmentTypes ?? []).map((g) => ({
    label: GARMENT_LABELS[g.garmentType] ?? g.garmentType,
    count: g.count,
  }))
  const payTypeItems = (breakdown?.payTypes ?? []).map((p) => ({
    label: PAY_TYPE_LABELS[p.payType] ?? p.payType,
    count: p.count,
  }))

  return (
    <>
      <Head>
        <title>ダッシュボード | Pittalinco</title>
      </Head>
      <Box component="main" sx={{ flexGrow: 1, py: 2 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <PageHeader
              title="ダッシュボード"
              subtitle="型紙ジェネレーターの売上・注文は決済確定日で集計しています"
              breadcrumbs={[{ label: 'ダッシュボード' }]}
              action={
                <PatternRangeSelector
                  value={rangeMode}
                  onChange={handleRangeModeChange}
                  customFrom={customFrom}
                  customTo={customTo}
                  onCustomChange={handleCustomChange}
                />
              }
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid xs={12} sm={6} lg={3}>
              <PatternKpiCard
                color="success.main"
                formatValue={formatYen}
                icon={<BanknotesIcon />}
                label="売上"
                loading={isLoading}
                previous={summary?.sales.previous ?? 0}
                value={summary?.sales.value ?? 0}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <PatternKpiCard
                color="primary.main"
                icon={<ShoppingBagIcon />}
                label="注文数"
                loading={isLoading}
                previous={summary?.orders.previous ?? 0}
                value={summary?.orders.value ?? 0}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <PatternKpiCard
                color="info.main"
                icon={<ArrowDownTrayIcon />}
                label="ダウンロード数"
                loading={isLoading}
                previous={summary?.downloads.previous ?? 0}
                value={summary?.downloads.value ?? 0}
              />
            </Grid>
            <Grid xs={12} sm={6} lg={3}>
              <PatternKpiCard
                color="warning.main"
                icon={<UsersIcon />}
                label="購入者数"
                loading={isLoading}
                previous={summary?.buyers.previous ?? 0}
                value={summary?.buyers.value ?? 0}
              />
            </Grid>

            <Grid xs={12} lg={8}>
              <PatternTrendChart loading={isLoading} timeseries={dashboard?.timeseries ?? []} />
            </Grid>
            <Grid xs={12} lg={4}>
              <PatternBreedRanking breeds={breakdown?.breeds ?? []} loading={isLoading} />
            </Grid>

            <Grid xs={12} md={6} lg={4}>
              <PatternDonutChart items={garmentItems} loading={isLoading} title="型紙の種類" />
            </Grid>
            <Grid xs={12} md={6} lg={4}>
              <PatternDonutChart items={payTypeItems} loading={isLoading} title="決済方法" />
            </Grid>

            <Grid xs={12}>
              <PatternLatestOrders loading={isOrdersLoading} orders={latestOrders} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  )
}

Page.getLayout = (page) => <DashboardLayout>{page}</DashboardLayout>

export default Page
